import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { StaticImage } from 'gatsby-plugin-image';
import { IS_PRO } from 'gatsby-theme-hem-store/src/constants';
import { transition } from 'hem-ui';
import Cookies from 'js-cookie';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { MarketingCommunicationForms } from '../MarketingCommunicationForms';

export const NewsletterPopup: React.FC<{
  pathname: string;
}> = ({ pathname }): JSX.Element => {
  const [step, setStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const hasSeenNewsletterPopup = Cookies.get('has-seen-newsletter-popup');
    const newsletterTimeout = window.setTimeout(() => {
      if (hasSeenNewsletterPopup === undefined) {
        // search the dom if there's a div with a class
        // that starts with RichTextNewsletterSignupForm
        const newsletterForm = document.querySelector(
          'div[class^="RichTextNewsletterSignupForm"]',
        );

        // if there's no newsletter form on
        // this page, open the popup
        if (newsletterForm === null) {
          setIsOpen(true);
          Cookies.set('has-seen-newsletter-popup', 'true', { expires: 7 });
        }
      }
    }, 120000);

    return () => window.clearTimeout(newsletterTimeout);
  }, [pathname]);

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const onNewsletterSubmitSuccess = (): void => {
    setStep(1);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-10 bg-[rgba(0,0,0,0.25)] data-[state=open]:animate-overlay-show" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-20 w-[calc(100vw_-_2rem)] max-w-screen-md translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded bg-white shadow data-[state=open]:animate-content-show focus:outline-none">
          <div className="grid md:grid-cols-[auto_1fr]">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                className="hidden md:block"
                key={step}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                {...{ transition }}
              >
                {step === 0 ? (
                  <StaticImage
                    src="./step1-image.jpg"
                    alt=""
                    width={300}
                    aspectRatio={0.66666667}
                    layout="constrained"
                    placeholder="blurred"
                    quality={100}
                  />
                ) : null}
                {step === 1 ? (
                  <StaticImage
                    src="./step2-image.jpg"
                    alt=""
                    width={300}
                    aspectRatio={0.66666667}
                    layout="constrained"
                    placeholder="blurred"
                    quality={100}
                  />
                ) : null}
              </motion.div>
            </AnimatePresence>

            <div className="p-6">
              <MarketingCommunicationForms
                onNewsletterSubmitSuccess={onNewsletterSubmitSuccess}
                form="both"
                newsletterTopTitle="Weekly inbox inspiration"
                newsletterTitle="Sign up to the newsletter!"
                newsletterContent={
                  IS_PRO
                    ? 'Get updates on sales and campaigns, exclusive pre-access to product launches, and weekly inspiration.'
                    : 'Get a 10% discount on your first order, updates on sales and campaigns, exclusive pre-access to product launches, and weekly inspiration.'
                }
                smsTopTitle="Let’s exchange numbers!"
                smsTitle="Exclusive SMS membership"
                smsContent="Enter your mobile number to get an additional 15% off one purchase, exclusive membership deals, early access to product launches and more!"
                location="Popup"
              />
            </div>
          </div>

          <Dialog.Close asChild>
            <button
              className="absolute right-0 top-0 inline-flex h-12 w-12 appearance-none items-center justify-center rounded-bl rounded-tr transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} className="text-black-400" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default NewsletterPopup;
